import { fetchApi } from '@/services/api';

import type {
  UsageByGroupResponse,
  UsageEvolutionResponse,
  UsageFiltersResponse,
  UsageGranularity,
  UsageKpisResponse,
  UsageLineRow,
  UsageOrganizationsResponse,
  UsagePaginated,
  UsageQueryParams,
  UsageSupplierDayRow,
} from '../types';

const BASE = 'usage-monitoring';

function toParams(
  params: UsageQueryParams = {},
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (params.date_from) out.date_from = params.date_from;
  if (params.date_to) out.date_to = params.date_to;
  if (params.warehouse != null) out.warehouse = params.warehouse;
  if (params.supplier != null) out.supplier = params.supplier;
  if (params.user != null) out.user = params.user;
  if (params.status) out.status = params.status;
  if (params.presence_status) out.presence_status = params.presence_status;
  if (params.order_date) out.order_date = params.order_date;
  if (params.granularity) out.granularity = params.granularity;
  if (params.page != null) out.page = params.page;
  if (params.page_size != null) out.page_size = params.page_size;
  if (params.organization != null) out.organization = params.organization;
  if (params.group_by) out.group_by = params.group_by;
  if (params.nocache) out.nocache = 1;
  return out;
}

export const usageMonitoringService = {
  getKpis(params: UsageQueryParams = {}, options?: { signal?: AbortSignal }) {
    return fetchApi<UsageKpisResponse>(`${BASE}/kpis/`, {
      params: toParams(params),
      signal: options?.signal,
    });
  },

  getFilters(params: UsageQueryParams = {}, options?: { signal?: AbortSignal }) {
    return fetchApi<UsageFiltersResponse>(`${BASE}/filters/`, {
      params: toParams(params),
      signal: options?.signal,
    });
  },

  getEvolution(
    params: UsageQueryParams & { granularity?: UsageGranularity } = {},
    options?: { signal?: AbortSignal },
  ) {
    return fetchApi<UsageEvolutionResponse>(`${BASE}/evolution/`, {
      params: toParams({ granularity: 'month', ...params }),
      signal: options?.signal,
    });
  },

  getSupplierDays(
    params: UsageQueryParams = {},
    options?: { signal?: AbortSignal },
  ) {
    return fetchApi<UsagePaginated<UsageSupplierDayRow>>(`${BASE}/supplier-days/`, {
      params: toParams(params),
      signal: options?.signal,
    });
  },

  getLines(params: UsageQueryParams = {}, options?: { signal?: AbortSignal }) {
    return fetchApi<UsagePaginated<UsageLineRow>>(`${BASE}/supplier-days/lines/`, {
      params: toParams(params),
      signal: options?.signal,
    });
  },

  getByGroup(params: UsageQueryParams = {}, options?: { signal?: AbortSignal }) {
    return fetchApi<UsageByGroupResponse>(`${BASE}/by-group/`, {
      params: toParams(params),
      signal: options?.signal,
    });
  },

  getOrganizations(options?: { signal?: AbortSignal }) {
    return fetchApi<UsageOrganizationsResponse>(`${BASE}/organizations/`, {
      signal: options?.signal,
    });
  },
};
